import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Exam, Plus } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { toast } from "@/lib/toast";
import { AssignmentCard } from "@/components/therapist/tests/AssignmentCard";
import { AssignmentResultDialog } from "@/components/therapist/tests/AssignmentResultDialog";
import { AssignmentFilters } from "@/components/therapist/tests/AssignmentFilters";
import { AssignmentSummary } from "@/components/therapist/tests/AssignmentSummary";
import { SendTestDialog } from "@/components/therapist/tests/SendTestDialog";
import { throwIfNotOk } from "@/lib/api-error";
import type { AssignmentStatus, TestAssignment } from "@/types/test";

export const Route = createFileRoute("/dashboard/therapist/tests")({
  component: TherapistTestsPage,
});

function TherapistTestsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AssignmentStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["therapist", "test-assignments", search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      const res = await fetch(`/api/test-assignments?${params}`);
      await throwIfNotOk(res, t("tests.therapist.fetchError"));
      return res.json() as Promise<{ assignments: TestAssignment[] }>;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/test-assignments/${id}`, { method: "DELETE" });
      await throwIfNotOk(res, t("tests.therapist.deleteError"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["therapist", "test-assignments"] });
      toast.success(t("tests.therapist.deleted"));
    },
  });

  const assignments = useMemo(() => data?.assignments ?? [], [data]);
  const filtered = useMemo(
    () => (status === "all" ? assignments : assignments.filter((a) => a.status === status)),
    [assignments, status],
  );

  return (
    <div className="relative mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1
            className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {t("tests.therapist.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("tests.therapist.subtitle")}</p>
        </div>

        <button
          onClick={() => setSendOpen(true)}
          className="group inline-flex shrink-0 items-center gap-2 self-start rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm shadow-primary/20 transition-all hover:shadow-md hover:shadow-primary/30 hover:brightness-110 active:scale-[0.98] sm:self-auto"
        >
          <Plus className="size-4 transition-transform group-hover:rotate-90" weight="bold" />
          {t("tests.therapist.send")}
        </button>
      </div>

      <AssignmentSummary assignments={assignments} isLoading={isLoading} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 items-center gap-3">
          <Exam className="size-5 shrink-0 text-primary" />
          <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
            {t("tests.therapist.sectionTitle")}
          </h2>
          {isLoading && <div className="size-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />}
        </div>

        <div className="sm:ml-auto">
          <AssignmentFilters
            status={status}
            search={search}
            onStatusChange={setStatus}
            onSearchChange={setSearch}
          />
        </div>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card px-5 py-3 shadow-sm sm:px-6 sm:py-4">
        {isLoading && (
          <p className="py-12 text-center text-sm text-muted-foreground">{t("common.loading")}</p>
        )}

        {!isLoading && filtered.length === 0 && (
          <p className="py-12 text-center text-sm text-muted-foreground">{t("tests.therapist.empty")}</p>
        )}

        {!isLoading &&
          filtered.map((a) => (
            <AssignmentCard
              key={a.id}
              assignment={a}
              onView={setViewingId}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ))}
      </div>

      <SendTestDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ["therapist", "test-assignments"] })}
      />
      <AssignmentResultDialog assignmentId={viewingId} onClose={() => setViewingId(null)} />
    </div>
  );
}
